<template>
    <section class="image-showcase-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
                <p v-if="subtitle" class="section-subtitle">{{ subtitle }}</p>
            </div>

            <div class="features-showcase-container">
                <div
                    v-for="(feature, index) in features"
                    :key="index"
                    class="feature-showcase"
                    :class="{ reverse: index % 2 !== 0 }"
                >
                    <div class="feature-content">
                        <template v-if="index % 2 === 0">
                            <div class="feature-text">
                                <h3 class="feature-title">{{ feature.title }}</h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img 
                                        :src="feature.imageUrl" 
                                        :alt="feature.title"
                                        loading="lazy"
                                        class="feature-image-preview"
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="feature-image">
                                <div class="image-container">
                                    <img 
                                        :src="feature.imageUrl" 
                                        :alt="feature.title"
                                        loading="lazy"
                                        class="feature-image-preview"
                                    />
                                </div>
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">{{ feature.title }}</h3>
                                <p class="feature-description">
                                    {{ feature.description }}
                                </p>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
interface Feature {
    title: string
    description: string
    imageUrl: string
}

interface Props {
    title: string
    subtitle?: string
    features: Feature[]
}

defineProps<Props>()
</script>

<style lang="less" scoped>
.image-showcase-section {
    padding: 11.25rem 0;
    background-color: #120e11;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.section-subtitle {
    font-size: 1.125rem;
    color: #adadad;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.6;
}

.features-showcase-container {
    max-width: 1200px;
    margin: 0 auto;
    border-radius: 20px;
    overflow: hidden;

    .feature-showcase:first-of-type {
        background: linear-gradient(180deg, #341012, #220f11, #190f11, #100e11);
    }

    .feature-showcase:not(:first-of-type):not(:last-of-type) {
        background-color: #120e11;
    }

    .feature-showcase:last-of-type {
        background: linear-gradient(0deg, #341012, #220f11, #190f11, #100e11);
        border-radius: 20px;
    }
}

.feature-showcase {
    padding: 5rem;

    &:last-of-type {
        margin-bottom: 4rem;
    }
}

.feature-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
}

.feature-text {
    padding: 2rem;
}

.feature-title {
    font-size: 2.25rem;
    font-weight: 500;
    margin-bottom: 1.5rem;
    color: white;
    line-height: 1.3;
}

.feature-description {
    color: #adadad;
    line-height: 1.6;
    font-size: 1.125rem;
    margin: 0;
}

.feature-image {
    position: relative;
}

.image-container {
    border-radius: 20px;
    overflow: hidden;
}

.feature-image-preview {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 20px;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.02);
    }
}

@media (max-width: 768px) {
    .image-showcase-section {
        padding: 5rem 0;
    }

    .section-title {
        font-size: 2rem;
        padding: 0 1rem;
    }

    .section-subtitle {
        font-size: 1rem;
        padding: 0 1rem;
    }

    .section-header {
        margin-bottom: 2rem;
        padding: 0 1rem;
    }

    .features-showcase-container {
        padding: 0 1rem;
    }

    .feature-content {
        grid-template-columns: 1fr;
        gap: 2rem;
    }

    .feature-showcase {
        padding: 3rem 1.5rem;
    }

    .feature-text {
        padding: 1rem;
    }

    .feature-title {
        font-size: 1.5rem;
        margin-bottom: 1rem;
    }

    .feature-description {
        font-size: 1rem;
    }
}

@media (max-width: 480px) {
    .image-showcase-section {
        padding: 3rem 0;
    }

    .section-title {
        font-size: 1.5rem;
    }

    .section-header {
        margin-bottom: 1.5rem;
    }

    .features-showcase-container {
        padding: 0 0.75rem;
    }

    .feature-showcase {
        padding: 2rem 1rem;
    }

    .feature-title {
        font-size: 1.25rem;
    }

    .feature-description {
        font-size: 0.875rem;
    }
}
</style>

